/**
 * procedureFlow.js
 *
 * Frontend-only step linking + loop detection helpers for the Procedure builder.
 *
 * These functions work against a normalized "flow step" shape so the loop
 * detection logic can be unit tested without any Vue/store dependencies:
 *
 *   flowStep = {
 *     id: <string>,              // question id
 *     links: [
 *       {
 *         key: 'default' | 'yes' | 'no',
 *         action: 'next' | 'link' | 'end',
 *         target: <questionId> | 'end' | null
 *       },
 *       ...
 *     ]
 *   }
 *
 * `buildFlowSteps` converts real procedure question objects into this shape.
 * `detectFlowIssues` is the pure validation function.
 */

// ─── Normalization ──────────────────────────────────────────────────────────

/**
 * Converts the app's question objects into the normalized flow-step shape
 * used by detectFlowIssues.
 *
 * @param {Array} questions - procedureForEdit.questions
 * @param {Function} getTypeName - (typeId) => 'YesOrNo' | 'TextInput' | ...
 * @returns {Array} normalized flow steps, in the same order as questions
 */
export function buildFlowSteps (questions, getTypeName) {
  var list = questions || []
  return list.map(function (question) {
    var typeName = getTypeName(question.typeId)
    var links = []

    if (typeName === 'YesOrNo') {
      links.push(legacyResultToLink('yes', question.yesResult))
      links.push(legacyResultToLink('no', question.noResult))
    } else {
      var flowAction = question.flowAction || 'next'
      var flowTarget = question.flowTarget != null ? question.flowTarget : null

      // Legacy data may have used flowTarget === 'end' (from a since-removed
      // "END Procedure" option in the Link-to dropdown). Normalize it to the
      // dedicated 'end' action.
      if (flowTarget === 'end') {
        flowAction = 'end'
        flowTarget = null
      }

      links.push({
        key: 'default',
        action: flowAction,
        target: flowTarget
      })
    }

    return {
      id: question.id,
      links: links
    }
  })
}

function legacyResultToLink (key, result) {
  if (result && result.type === 'goto') {
    return { key: key, action: 'link', target: result.value != null ? result.value : null }
  }
  if (result && (result.type === 'NA' || result.type === 'end')) {
    return { key: key, action: 'end', target: null }
  }
  // Yes/No branches have no implicit "next step" concept - an unset branch
  // (no legacy result recorded yet) is treated as an unresolved link so
  // detectFlowIssues flags it as a missing destination until the user
  // explicitly links it or sets it to END.
  return { key: key, action: 'link', target: null }
}

/**
 * Ensures a non-branching question has the new flow fields present,
 * defaulting from any legacy yesResult if one already exists.
 * Mutates the question in place using Vue.set-style assignment via the
 * supplied `setter` (so callers can use this.$set for reactivity).
 */
export function ensureFlowFields (question, setter) {
  var set = setter || function (obj, key, val) { obj[key] = val }
  if (question.flowAction === undefined) {
    var initialAction = 'next'
    var initialTarget = null
    if (question.yesResult && question.yesResult.type === 'goto') {
      initialAction = 'link'
      initialTarget = question.yesResult.value != null ? question.yesResult.value : null
    } else if (question.yesResult && (question.yesResult.type === 'NA' || question.yesResult.type === 'end')) {
      initialAction = 'end'
    }
    set(question, 'flowAction', initialAction)
  }
  if (question.flowTarget === undefined) {
    var legacyTarget = null
    if (question.flowAction === 'link' && question.yesResult && question.yesResult.type === 'goto') {
      legacyTarget = question.yesResult.value != null ? question.yesResult.value : null
    }
    set(question, 'flowTarget', legacyTarget)
  }
  // Migrate legacy data: "END Procedure" used to be a selectable value in
  // the Link-to dropdown (flowAction: 'link', flowTarget: 'end'). Ending a
  // procedure is now handled exclusively via the dedicated END button, so
  // normalize any legacy records to flowAction: 'end'.
  if (question.flowTarget === 'end') {
    set(question, 'flowAction', 'end')
    set(question, 'flowTarget', null)
  }
}

/**
 * Mirrors a non-branching question's flowAction/flowTarget into the legacy
 * yesResult shape the procedure runners (ProcedureNavigator, EventAdd,
 * ExecuteRoutineTask) understand. Runners only advance on
 * { type: 'goto', value: id }; anything else stops the procedure.
 *   link -> goto target, end -> NA, next -> goto following step (NA if last).
 * Yes/No questions are left untouched. Mutates questions in place.
 */
export function mirrorFlowToLegacy (questions, getTypeName) {
  var list = questions || []
  list.forEach(function (question, index) {
    if (getTypeName(question.typeId) === 'YesOrNo' || question.flowAction === undefined) {
      return
    }
    var action = question.flowAction
    var target = question.flowTarget
    if (target === 'end') {
      action = 'end'
    }
    var result = { type: 'NA', value: null }
    if (action === 'link' && target) {
      result = { type: 'goto', value: target }
    } else if (action === 'next' && list[index + 1]) {
      result = { type: 'goto', value: list[index + 1].id }
    } else if (action === 'link') {
      // Unresolved destination: keep whatever legacy value existed.
      return
    }
    question.yesResult = result
  })
  return list
}

// ─── Loop / issue detection ─────────────────────────────────────────────────

/**
 * Pure function: given normalized flow steps (in order), returns an array
 * of issues describing missing destinations, self-loops and chain loops.
 *
 * issue = {
 *   stepId, key, severity: 'error' | 'warning', code, message
 * }
 */
export function detectFlowIssues (steps) {
  var list = steps || []
  var issues = []
  var indexById = {}
  list.forEach(function (step, index) { indexById[step.id] = index })

  function resolve (step, link) {
    if (link.action === 'end') {
      return { type: 'end' }
    }
    if (link.action === 'next') {
      var idx = indexById[step.id]
      var nextStep = list[idx + 1]
      return nextStep ? { type: 'step', id: nextStep.id } : { type: 'end' }
    }
    if (link.action === 'link') {
      if (!link.target) {
        return { type: 'missing' }
      }
      if (link.target === 'end') {
        return { type: 'end' }
      }
      if (indexById[link.target] === undefined) {
        return { type: 'invalid' }
      }
      return { type: 'step', id: link.target }
    }
    return { type: 'end' }
  }

  // Which steps can reach END following EVERY outgoing branch (fixed point).
  var reachesEnd = {}
  var changed = true
  while (changed) {
    changed = false
    list.forEach(function (step) {
      if (reachesEnd[step.id]) {
        return
      }
      // Only a real END (or continuing past the last step) counts; missing
      // or invalid destinations never count as a way out.
      var ok = step.links.some(function (link) {
        var r = resolve(step, link)
        if (r.type === 'end') {
          return true
        }
        return r.type === 'step' && reachesEnd[r.id] === true
      })
      if (ok) {
        reachesEnd[step.id] = true
        changed = true
      }
    })
  }

  // Whether `fromId` can reach `goalId` following every branch.
  function canReach (fromId, goalId) {
    var seen = {}
    var stack = [fromId]
    while (stack.length) {
      var id = stack.pop()
      if (id === goalId) {
        return true
      }
      if (seen[id] || indexById[id] === undefined) {
        continue
      }
      seen[id] = true
      var s = list[indexById[id]]
      s.links.forEach(function (link) {
        var r = resolve(s, link)
        if (r.type === 'step') {
          stack.push(r.id)
        }
      })
    }
    return false
  }

  list.forEach(function (step) {
    step.links.forEach(function (link) {
      if (link.action !== 'link') {
        return
      }
      var resolved = resolve(step, link)

      if (resolved.type === 'missing') {
        issues.push({
          stepId: step.id,
          key: link.key,
          severity: 'warning',
          code: 'missing-destination',
          message: 'Missing destination. "Go to step" is selected but no step has been chosen.'
        })
        return
      }

      if (resolved.type === 'invalid') {
        issues.push({
          stepId: step.id,
          key: link.key,
          severity: 'error',
          code: 'invalid-target',
          message: 'The chosen step no longer exists. Choose another step.'
        })
        return
      }

      if (resolved.type !== 'step') {
        return
      }

      // A branch resolving directly to its own step is always an error
      // (matches server ProcedureFlowValidator), regardless of other branches.
      if (resolved.id === step.id) {
        issues.push({
          stepId: step.id,
          key: link.key,
          severity: 'error',
          code: 'self-loop',
          message: 'This step goes back to itself, creating an infinite loop.'
        })
        return
      }

      // Chain loop is only an error when it is ENDLESS: the cycle has no
      // path to END. If any step in the cycle can reach END, then so can
      // this step, so a retry loop is not flagged.
      if (!reachesEnd[step.id] && canReach(resolved.id, step.id)) {
        issues.push({
          stepId: step.id,
          key: link.key,
          severity: 'error',
          code: 'chain-loop',
          message: 'Following these steps eventually loops back to this step, creating an infinite loop.'
        })
      }
    })
  })

  return issues
}

/**
 * Returns 'error' | 'warning' | 'ok' for a given step id based on the
 * issues array produced by detectFlowIssues.
 */
export function getStepState (issues, stepId) {
  var stepIssues = (issues || []).filter(function (i) { return i.stepId === stepId })
  if (stepIssues.some(function (i) { return i.severity === 'error' })) {
    return 'error'
  }
  if (stepIssues.some(function (i) { return i.severity === 'warning' })) {
    return 'warning'
  }
  return 'ok'
}

/**
 * Whether the overall procedure has any blocking (error) issues.
 */
export function hasBlockingIssues (issues) {
  return (issues || []).some(function (i) { return i.severity === 'error' })
}

/**
 * Maps the "issues" array returned by the server on a rejected save
 * (POST /api/company/{companyId}/procedures -> 400 { issues: [...] }) into
 * the same shape ValidationSidebar/detectFlowIssues issues use, so server
 * issues can be merged into the sidebar list and jumped to like any other
 * flow issue.
 *
 * Server issue shape:
 *   { severity: 'error'|'warning', code, questionId, branch: 'default'|'yes'|'no', message }
 *
 * @param {Array} serverIssues - e.response.data.issues from the failed save
 * @param {Array} questions - procedureForEdit.questions (current, in order)
 * @returns {Array} issues shaped like { stepId, key, severity, code, message, stepLabel }
 */
export function mapServerIssuesToSidebarIssues (serverIssues, questions) {
  var list = serverIssues || []
  var qList = questions || []
  var indexById = {}
  qList.forEach(function (q, idx) { indexById[q.id] = idx })

  return list.map(function (issue) {
    var idx = indexById[issue.questionId]
    var stepLabel = idx !== undefined ? ('Step ' + (idx + 1)) : 'Unknown step'
    return {
      stepId: issue.questionId,
      key: issue.branch || 'default',
      severity: issue.severity,
      code: issue.code,
      message: issue.message,
      stepLabel: stepLabel
    }
  })
}

/**
 * Builds the small connector descriptor shown between two step cards for a
 * non-branching step's single link.
 *
 * @returns {{ state: 'seq'|'linked'|'warning'|'loop', label: string }}
 */
export function describeConnector (step, issues, questionsById) {
  if (!step || !step.links || !step.links.length) {
    return { state: 'seq', label: '↓ Next Step' }
  }
  var link = step.links[0]
  var stepIssues = (issues || []).filter(function (i) { return i.stepId === step.id && i.key === link.key })

  if (stepIssues.some(function (i) { return i.code === 'self-loop' || i.code === 'chain-loop' })) {
    return { state: 'loop', label: '⚠ Loop detected' }
  }
  if (stepIssues.some(function (i) { return i.code === 'missing-destination' })) {
    return { state: 'warning', label: '⚠ Destination unset' }
  }
  if (link.action === 'end') {
    return { state: 'seq', label: '⊘ Ends here' }
  }
  if (link.action === 'link') {
    var target = questionsById && questionsById[link.target]
    var label = link.target === 'end' ? 'END' : (target ? target.label : ('Step ' + link.target))
    return { state: 'linked', label: '→ Goes to ' + label }
  }
  return { state: 'seq', label: '↓ Next Step' }
}

/**
 * Builds the connector descriptor for a Yes/No step, explicitly describing
 * where each branch goes.
 *
 * @returns {{ state: 'seq'|'linked'|'detached'|'warning'|'loop', label: string }}
 */
export function describeYesNoConnector (flowStep, issues, questionsById, nextStepId) {
  if (!flowStep || !flowStep.links || !flowStep.links.length) {
    return { state: 'seq', label: '↓ Next Step' }
  }
  var stepIssues = (issues || []).filter(function (i) {
    return i.stepId === flowStep.id && (i.key === 'yes' || i.key === 'no')
  })
  if (stepIssues.some(function (i) { return i.severity === 'error' })) {
    return { state: 'loop', label: '⚠ Loop detected' }
  }
  if (stepIssues.some(function (i) { return i.severity === 'warning' })) {
    return { state: 'warning', label: '⚠ Branch destination unset' }
  }
  var flowsNext = false
  var hasNext = nextStepId !== null && nextStepId !== undefined
  function branchText (key) {
    var link = null
    for (var i = 0; i < flowStep.links.length; i++) {
      if (flowStep.links[i].key === key) { link = flowStep.links[i] }
    }
    if (!link) { return '?' }
    if (link.action === 'end') { return 'END' }
    if (link.action === 'link') {
      if (hasNext && link.target === nextStepId) {
        flowsNext = true
        return 'next step'
      }
      var target = questionsById && questionsById[link.target]
      return target ? target.label : ('Step ' + link.target)
    }
    return '?'
  }
  var yesText = branchText('yes')
  var noText = branchText('no')
  return {
    state: flowsNext ? 'linked' : 'detached',
    label: 'Yes → ' + yesText + ' · No → ' + noText
  }
}
